import React, { FormEvent, useCallback, useState } from 'react';
import { Button, Input, InputTime, Modal } from '@cognitiv/cyprus-ui';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectJob } from 'ducks/job/selectors';
import { JobState } from 'ducks/job/slices';
import { editJob } from 'ducks/jobs/operators';
import { selectEditJob } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { updateSuccess } from 'ducks/settings/slices';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useValidate } from 'hooks/useValidation';
import { edit_job_default_state, edit_job_validate } from 'modals/job/constants';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';
import { handleTimeoutConversion } from 'utils/helpers';
import { mergeSearchQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';

import cn from 'modals/Modal.module.scss';

export const EditJob = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { theme } = useSwitchTheme();

  const job = useAppSelector(selectJob);
  const edit_job = useAppSelector(selectEditJob, shallowEqual);

  const [form, setForm] = useState<JobState>({ ...edit_job_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, edit_job_validate);

  const closeModal = useCallback(() => {
    dispatch(setModal({ edit_job: false }));
  }, [dispatch]);

  const onChange = useCallback(
    (item: Partial<JobState>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        const { job_id } = job;

        const queries = mergeSearchQuery(search, { update: uuidv4() });

        if (!job_id) return;

        const res: any = await editJob(job_id, form);
        if (res?.error) return;

        closeModal();
        navigate(`${pathname}${queries}`);
        dispatch(updateSuccess(`Job has been updated successfully`));
      } catch (err) {
        handleError(err);
      }
    },
    [closeModal, dispatch, form, job, navigate, pathname, search, validate],
  );

  const { batch_descriptor, timeout_seconds_masked } = form;

  const { job_id, job_definition_name } = job;

  return (
    <Modal name="edit_job" show={edit_job} width={600} padding={0} close_box={50} onClose={closeModal} standard={false}>
      <h2
        className={cn.header}
        style={{
          backgroundColor: theme.element_background,
          color: theme.font_color,
          borderBottom: theme.border,
        }}
      >
        Edit Job: {job_id} {job_definition_name}
      </h2>
      <form className={cn.body} onSubmit={onSubmit}>
        <Input
          auto_focus
          label="Batch Descriptor"
          value={batch_descriptor}
          onChange={(item: string) => onChange({ batch_descriptor: item })}
          error_message={errors.find((error) => error.key === 'batch_descriptor')?.error}
          error_level={getValidationError({ errors, key: 'batch_descriptor' }) ? ERROR_LEVEL.ERROR : undefined}
        />
        <InputTime
          text_align="left"
          label="Timeout (hh:mm:ss)"
          value={timeout_seconds_masked}
          margin="10px 0px 0px 0px"
          onChange={(input: { value: string }) => {
            onChange({ timeout_seconds: handleTimeoutConversion(input.value), timeout_seconds_masked: input.value });
          }}
          error_message={errors.find((error) => error.key === 'timeout_seconds')?.error}
          error_level={getValidationError({ errors, key: 'timeout_seconds' }) ? ERROR_LEVEL.ERROR : undefined}
        />
        <div className={cn.buttonContainer}>
          <Button type="submit" width={115} button_size="medium" margin="10px 0px 0px 0px">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
